'use client';

import {
  useState,
  useEffect,
  type JSX,
  type KeyboardEventHandler,
} from 'react';

import { wrapArray } from '~/utils';
import { Show } from '~/components/show';
import { Badge } from '~/components/badge';
import { Input } from '~/components/input';
import { Button } from '~/components/button';
import { Skeleton } from '~/components/skeleton';
import { HStack, Stack } from '~/components/stack';
import { PlusCircleIcon } from '~/components/icons';
import {
  Chip,
  chipVariants,
  type IChipProps,
  type IChipOptionProps,
} from '~/components/chip';

function appendOptions(
  options: IChipOptionProps[],
  value: string[],
  customCategories: string[] = [],
): IChipOptionProps[] {
  const groupedOptions = options.reduce<Record<string, IChipOptionProps>>(
    (result, opt) => {
      result[opt.value] = opt;
      return result;
    },
    {},
  );

  const allOptions = [...options];
  customCategories.forEach((category) => {
    if (!groupedOptions[category]) {
      allOptions.push({
        value: category,
        label: category,
        removeable: true,
      });
      groupedOptions[category] = allOptions[allOptions.length - 1];
    }
  });

  return value.reduce<IChipOptionProps[]>((result, v) => {
    if (!groupedOptions[v]) {
      return [
        ...result,
        {
          value: v,
          label: v,
          removeable: true,
        },
      ];
    }
    return result;
  }, allOptions);
}

interface IChipPickerProps {
  value?: string[];
  isMulti?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  addButtonText?: string;
  disabledDelete?: boolean;
  disabledCreate?: boolean;
  options: IChipOptionProps[];
  customCategories?: string[];
  onCustomCategoryChange?: (category: string, action: 'add' | 'remove') => void;
  onChange?: (value: string | string[]) => void;
}

export function ChipPicker({
  options,
  onChange,
  value = [],
  isMulti = true,
  isLoading = false,
  disabledCreate = false,
  disabledDelete = false,
  customCategories = [],
  onCustomCategoryChange,
  addButtonText = 'Add other',
  placeholder = 'Enter your other advertising here',
}: IChipPickerProps): JSX.Element {
  value = wrapArray(!value ? undefined : value);
  const [opts, setOpts] = useState(
    appendOptions(options, value, customCategories),
  );
  const [createMode, setCreateMode] = useState(false);

  const [selected, setSelected] = useState<{
    values: string[];
    list: Record<string, string>;
  }>({
    values: value,
    list: value.reduce<Record<string, string>>((result, v) => {
      result[v] = v;

      return result;
    }, {}),
  });

  useEffect(() => {
    setOpts(appendOptions(options, value, customCategories));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const changeValues = (values: string[]) => {
    if (onChange) {
      if (isMulti) {
        onChange(values);
      } else {
        onChange(values[0]);
      }
    }
  };

  const handleSelect: IChipProps['onClick'] = (value) => {
    let { list } = selected;

    if (list[value]) {
      delete list[value];
    } else {
      if (isMulti) {
        list[value] = value;
      } else {
        list = {
          [value]: value,
        };
      }
    }

    setSelected({
      list,
      values: Object.values(list),
    });

    changeValues(Object.values(list));
  };

  const handleClickAdd = () => {
    setCreateMode(true);
  };

  const handleEnter: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      const { value } = e.currentTarget;
      setCreateMode(false);

      if (opts.find((opt) => opt.value === value)) {
        return;
      }

      if (onCustomCategoryChange) {
        onCustomCategoryChange(value, 'add');
      }

      setOpts((state) => {
        return [
          ...state,
          {
            value,
            label: value,
            removeable: true,
          },
        ];
      });

      setSelected((state) => {
        if (isMulti) {
          return {
            ...state,
            values: [...state.values, value],
            list: {
              ...state.list,
              [value]: value,
            },
          };
        } else {
          return {
            ...state,
            values: [value],
            list: {
              [value]: value,
            },
          };
        }
      });

      changeValues([...selected.values, value]);
    }
  };

  const handleRemove: IChipProps['onRemove'] = (value) => {
    if (disabledDelete) {
      return;
    }

    const isCustomCategory = customCategories.includes(value);

    if (isCustomCategory && onCustomCategoryChange) {
      onCustomCategoryChange(value, 'remove');
    }

    setOpts((state) => {
      return state.filter((opt) => opt.value !== value);
    });

    setSelected((state) => {
      delete state.list[value];

      return {
        ...state,
        list: state.list,
        values: state.values.filter((v) => v !== value),
      };
    });

    const newList = structuredClone(selected.list);
    delete newList[value];

    onChange?.(Object.values(newList));
  };

  return (
    <Stack>
      <Show when={!isLoading}>
        <HStack gap={2} className='flex-wrap'>
          {opts.map(({ value, removeable, ...rest }) => {
            return (
              <Chip
                key={value}
                value={value}
                onClick={handleSelect}
                onRemove={handleRemove}
                selected={!!selected.list[value]}
                className='hover:border-blueBonnet-50'
                removeable={!disabledDelete && removeable}
                {...rest}
              />
            );
          })}
          <Show when={!disabledCreate}>
            <Button
              variant='unstyled'
              onClick={handleClickAdd}
              className='[--svg-color:var(--color-blueBonnet-500)] text-blueBonnet-500 text-(length:--text-sm) font-normal items-center leading-[130%] p-0 h-[unset]'
            >
              <PlusCircleIcon className='stroke-white' />
              {addButtonText}
            </Button>
          </Show>
        </HStack>
        <Show when={!disabledCreate && createMode}>
          <Input placeholder={placeholder} onKeyDown={handleEnter} autoFocus />
        </Show>
      </Show>
      <Show when={isLoading}>
        <HStack gap={2} className='flex-wrap'>
          {Array.from({ length: 3 }).map((_, index) => {
            return (
              <Badge key={index} className={chipVariants()}>
                <Skeleton className='h-5 w-30' />
              </Badge>
            );
          })}
        </HStack>
      </Show>
    </Stack>
  );
}
